import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Database, FileText } from "lucide-react";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { BLOG_POSTS } from "@/lib/blog";
import { SECTION_OG, sectionImageMeta, SITE_ORIGIN } from "@/lib/ogSections";
import { hreflangLinks } from "@/lib/hreflang";
import { pageSchemaScript, PERSON_ID, PRACTICE_ID } from "@/seo/schema";

const URL = `${SITE_ORIGIN}/blog`;
const TITLE = "Building Technology Blog | Perimeter Security & AV";
const DESCRIPTION =
  "Practical articles on perimeter security system cost in India, electric fence installation in Delhi NCR and AV integration for command centres.";

export const Route = createFileRoute("/blog/")({
  component: BlogIndexPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      ...sectionImageMeta(SECTION_OG.askArif, "Building technology articles by Arif Khan"),
    ],
    links: [
      { rel: "canonical", href: URL },
      ...hreflangLinks("/blog"),
    ],
    scripts: [
      pageSchemaScript({
        path: "/blog",
        type: "CollectionPage",
        name: TITLE,
        description: DESCRIPTION,
        breadcrumb: [{ name: "Blog" }],
        extra: {
          about: [
            { "@type": "Thing", name: "Perimeter security" },
            { "@type": "Thing", name: "Electric fencing" },
            { "@type": "Thing", name: "AV integration" },
          ],
          hasPart: BLOG_POSTS.map((post) => ({
            "@type": "BlogPosting",
            "@id": `${SITE_ORIGIN}/blog/${post.slug}#article`,
            headline: post.title,
            description: post.description,
            url: `${SITE_ORIGIN}/blog/${post.slug}`,
            datePublished: post.publishedAt,
            dateModified: post.updatedAt,
            author: { "@id": PERSON_ID },
            publisher: { "@id": PRACTICE_ID },
          })),
        },
      }),
    ],
  }),
});

function BlogIndexPage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Blog"
        title="Field notes on security, fencing and AV integration."
        description="Useful, owner-facing guidance from the design decisions behind perimeter security systems, electric fencing and integrated command centres across India."
      />

      <section className="py-20">
        <div className="container-page grid gap-6 lg:grid-cols-3">
          {BLOG_POSTS.map((post, index) => (
            <Reveal key={post.slug} delay={index * 0.05}>
              <article className="h-full overflow-hidden rounded-2xl border border-border bg-surface transition-colors hover:border-border-strong">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={post.image} alt={post.title} loading={index === 0 ? "eager" : "lazy"} className="h-full w-full object-cover" />
                </div>
                <div className="flex h-full flex-col p-7">
                  <div className="flex items-center justify-between gap-4">
                    <span className="eyebrow text-accent">{post.category}</span>
                    <span className="text-xs text-ink-soft">{post.readTime}</span>
                  </div>
                  <h2 className="mt-4 text-2xl font-semibold leading-tight tracking-tight">{post.title}</h2>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-ink-soft">{post.excerpt}</p>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: post.slug }}
                    className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    Read the article <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-surface py-20">
        <div className="container-page grid gap-6 md:grid-cols-2">
          <a href="/llms.txt" className="group rounded-xl border border-border bg-background p-6 transition-colors hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <FileText className="h-5 w-5 text-primary" />
            <h2 className="mt-5 text-lg font-semibold">/llms.txt</h2>
            <p className="mt-2 text-sm leading-relaxed text-ink-soft">A plain-text index of Arif Khan’s expertise, pages and citable reference material.</p>
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium">Open feed <ArrowRight className="h-4 w-4" /></span>
          </a>
          <a href="/entities.json" className="group rounded-xl border border-border bg-background p-6 transition-colors hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Database className="h-5 w-5 text-primary" />
            <h2 className="mt-5 text-lg font-semibold">/entities.json</h2>
            <p className="mt-2 text-sm leading-relaxed text-ink-soft">Machine-readable entities connecting the practice, services, industries and articles.</p>
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium">Open feed <ArrowRight className="h-4 w-4" /></span>
          </a>
        </div>
      </section>

      <section className="py-20">
        <div className="container-page flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <span className="eyebrow">Need a project view?</span>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight">Let's Talk about the site in front of you.</h2>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            Let's Talk <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
